import axios from "axios";
import { eventService } from "../../services/eventService";
import toast from "react-hot-toast";

/**
 * Handles template selection and shows template content
 * @param {Object} template - The selected template
 * @param {Function} setSelectedTemplate - Function to set selected template
 * @param {Function} setTemplateTopic - Function to set template topic
 * @param {Function} setGeneratedContent - Function to set generated content
 * @param {Function} setFullTemplateTitle - Function to set full template title
 * @param {Function} setFullTemplateText - Function to set full template text
 * @param {Function} setOpenTemplateDialog - Function to open template dialog
 */
export const handleTemplateClick = (
  template,
  setSelectedTemplate,
  setTemplateTopic,
  setGeneratedContent,
  setFullTemplateTitle,
  setFullTemplateText,
  setOpenTemplateDialog
) => {
  setSelectedTemplate(template);
  // Clear any previous topic when selecting a new template
  setTemplateTopic("");

  // Show template text in preview panel
  setGeneratedContent(template.template);

  // Show the template dialog
  setFullTemplateTitle(template.title);
  setFullTemplateText(template.template);
  setOpenTemplateDialog(true);
};

/**
 * Handles using a template (close dialog and focus on topic input)
 * @param {Object} template - The template to use
 * @param {Function} setSelectedTemplate - Function to set selected template
 * @param {Function} setTemplateTopic - Function to set template topic
 * @param {Function} setGeneratedContent - Function to set generated content
 * @param {Function} setOpenTemplateDialog - Function to close template dialog
 * @param {Object} templateTopicRef - Ref to template topic input
 */
export const handleUseTemplate = (
  template,
  setSelectedTemplate,
  setTemplateTopic,
  setGeneratedContent,
  setOpenTemplateDialog,
  templateTopicRef
) => {
  setSelectedTemplate(template);
  setTemplateTopic("");
  setGeneratedContent(template.template);
  setOpenTemplateDialog(false);

  // Scroll to topic input after dialog closes
  setTimeout(() => {
    if (templateTopicRef.current) {
      templateTopicRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 100);
};

/**
 * Generates content from a selected template
 * @param {Object} selectedTemplate - The selected template
 * @param {string} templateTopic - The topic for the template
 * @param {Function} setTemplateLoading - Function to set template loading state
 * @param {Function} setGeneratedContent - Function to set generated content
 * @param {Function} setSelectedTemplate - Function to set selected template
 * @param {Function} setTemplateTopic - Function to set template topic
 * @returns {Promise<string>} The generated content
 */
export const handleGenerateFromTemplate = async (
  selectedTemplate,
  templateTopic,
  setTemplateLoading,
  setGeneratedContent,
  setSelectedTemplate,
  setTemplateTopic
) => {
  if (!selectedTemplate || !templateTopic) {
    toast.error("Please select a template and enter a topic");
    return;
  }

  setTemplateLoading(true);

  try {
    // Call our backend endpoint instead of Azure directly
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/ai/generate-from-template`,
      {
        template: selectedTemplate.template,
        topic: templateTopic,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    // Extract the generated content from the API response
    const generatedText = response.data.content;

    // Set the generated content in the UI
    setGeneratedContent(generatedText);

    // Emit event for credit tracking
    eventService.emit("CREDITS_UPDATED");

    // Clear template-specific state
    setSelectedTemplate(null);
    setTemplateTopic("");

    toast.success("Post generated successfully!");

    return generatedText;
  } catch (error) {
    console.error("Error generating post from template:", error);
    toast.error("Error generating post. Please try again.");
    throw error;
  } finally {
    setTemplateLoading(false);
  }
};

/**
 * Shows the full template content in a dialog
 * @param {Event} e - The click event
 * @param {Object} template - The template to view
 * @param {Function} setFullTemplateTitle - Function to set full template title
 * @param {Function} setFullTemplateText - Function to set full template text
 * @param {Function} setOpenTemplateDialog - Function to open template dialog
 */
export const handleViewFullTemplate = (
  e,
  template,
  setFullTemplateTitle,
  setFullTemplateText,
  setOpenTemplateDialog
) => {
  e.stopPropagation(); // Prevent triggering the card click
  setFullTemplateTitle(template.title);
  setFullTemplateText(template.template);
  setOpenTemplateDialog(true);
};
